/**
 * @file analytics.ts
 * @description Google Analytics 이벤트 추적 유틸리티 (SSOT)
 *
 * 모든 GA 이벤트 추적은 이 파일에서만 관리합니다.
 * 다른 컴포넌트는 이 파일의 함수만 import해서 사용합니다.
 *
 * 주요 기능:
 * 1. GA 이벤트 추적 공통 함수
 * 2. gtag/dataLayer 자동 감지 및 사용
 * 3. 일관된 이벤트 형식 제공
 * 4. 유입 경로 정보 자동 추가 옵션 (traffic-source.ts 사용)
 *
 * 사용 방법:
 * ```typescript
 * import { trackEvent } from '@/lib/utils/analytics';
 *
 * // 기본 사용
 * trackEvent('button_click', { button_name: 'cta' });
 *
 * // 유입 경로 정보 자동 포함
 * trackEvent('conversion', { value: 100 }, 'purchase', {
 *   includeTrafficSource: true
 * });
 * ```
 *
 * @dependencies
 * - window.gtag 또는 window.dataLayer (GA 스크립트 로드 필요)
 * - @/lib/utils/traffic-source (유입 경로 정보)
 */

import {
  getTrafficInfo,
  toGA4StandardFormat,
} from "@/lib/utils/traffic-source";

/**
 * GA 이벤트 추적 함수
 *
 * @param eventName 이벤트 이름 (예: 'button_click')
 * @param eventParams 이벤트 파라미터 (선택)
 * @param eventCategory 이벤트 카테고리 (기본값: 'user_action')
 * @param options 추적 옵션
 *   - includeTrafficSource: 유입 경로 정보를 자동으로 포함할지 여부 (기본값: false)
 *
 * @example
 * ```typescript
 * // 기본 사용 (유입 경로 정보 없음)
 * trackEvent('button_click', { button_name: 'cta' });
 *
 * // 유입 경로 정보 자동 포함
 * trackEvent('conversion', { value: 100 }, 'purchase', {
 *   includeTrafficSource: true
 * });
 * ```
 */
export function trackEvent(
  eventName: string,
  eventParams?: Record<string, unknown>,
  eventCategory: string = "user_action",
  options?: {
    includeTrafficSource?: boolean;
  },
): boolean {
  if (typeof window === "undefined") {
    console.warn("[GA Event] window가 정의되지 않았습니다.");
    return false;
  }

  const { includeTrafficSource = false } = options || {};

  // 유입 경로 정보 자동 추가 (traffic-source.ts에서 import)
  let enrichedParams = { ...eventParams };
  if (includeTrafficSource) {
    try {
      const { fbclid, trafficSource } = getTrafficInfo();

      // GA4 표준 형식으로 변환
      const ga4Standard = toGA4StandardFormat(trafficSource);

      // GA4 표준 파라미터와 커스텀 파라미터 모두 포함
      enrichedParams = {
        ...enrichedParams,
        // GA4 표준 파라미터 (채널 그룹 인식용)
        source: ga4Standard.source,
        medium: ga4Standard.medium,
        // 커스텀 파라미터 (상세 분석용)
        traffic_source: trafficSource.source,
        traffic_medium: trafficSource.medium,
        is_meta_ad: !!fbclid || trafficSource.isMeta,
      };

      // campaign이 있을 때만 추가 (빈 문자열 방지)
      if (trafficSource.campaign && trafficSource.campaign.trim() !== "") {
        enrichedParams.campaign = trafficSource.campaign;
        enrichedParams.traffic_campaign = trafficSource.campaign;
      }

      // fbclid가 있을 때만 추가
      if (fbclid) {
        enrichedParams.fbclid = fbclid;
      }
    } catch (error) {
      // 유입 경로 정보 가져오기 실패 시 원본 파라미터만 사용
      console.warn(
        "[GA Event] 유입 경로 정보 가져오기 실패, 원본 파라미터만 사용:",
        error,
      );
    }
  }

  const params = {
    event_category: eventCategory,
    ...enrichedParams,
  };

  console.log(`[GA Event] ${eventName}`, params);

  // 리포트 이벤트는 항상 dataLayer에 직접 푸시 (GA 비활성화 경로에서도 추적되도록)
  // 리포트 이벤트: report_view 카테고리 또는 리포트 관련 이벤트명
  const isReportEvent =
    eventCategory === "report_view" ||
    eventName.includes("report") ||
    eventName.includes("analysis");

  if (isReportEvent) {
    // 리포트 이벤트는 gtag 함수를 통해 직접 전송 (가장 확실한 방법)
    if (typeof window.gtag === "function") {
      try {
        // gtag가 no-op인지 확인
        const gtagString = window.gtag.toString();
        const isNoOp = gtagString.includes("🚫") || gtagString.includes("차단");

        if (!isNoOp) {
          // 정상적인 gtag 함수인 경우 직접 호출
          window.gtag("event", eventName, params);
          console.log(
            `[GA Event] ✅ 리포트 이벤트 gtag 호출 완료: ${eventName}`,
            params,
          );
          return true;
        }
      } catch (error) {
        console.error("[GA Event] ❌ 리포트 이벤트 gtag 호출 실패:", error);
      }
    }

    // gtag가 없거나 no-op인 경우 dataLayer.push 사용 (fallback)
    if (window.dataLayer && Array.isArray(window.dataLayer)) {
      try {
        const eventData = {
          event: eventName,
          ...params,
        };

        // push 전 길이 저장
        const lengthBefore = window.dataLayer.length;

        // dataLayer.push가 차단되었는지 확인
        const pushString = window.dataLayer.push.toString();
        const isBlocked =
          pushString.includes("🚫") || pushString.includes("차단");

        if (isBlocked) {
          // 차단된 경우, 원본 Array.prototype.push를 사용하여 직접 추가
          console.log(
            "[GA Event] 리포트 이벤트: 차단된 dataLayer.push 감지, 원본 push 사용",
          );
          Array.prototype.push.call(window.dataLayer, eventData);
        } else {
          // 정상적인 경우 일반 push 사용
          window.dataLayer.push(eventData);
        }

        // push 후 길이 확인
        const lengthAfter = window.dataLayer.length;
        const lastItem = window.dataLayer[lengthAfter - 1] as
          | Record<string, unknown>
          | undefined;

        console.log(
          `[GA Event] ✅ 리포트 이벤트 dataLayer에 푸시 완료: ${eventName}`,
          {
            lengthBefore,
            lengthAfter,
            isLengthIncreased: lengthAfter > lengthBefore,
            isLastItemMatch: lastItem && lastItem.event === eventName,
            eventData,
          },
        );
        return true;
      } catch (error) {
        console.error(
          "[GA Event] ❌ 리포트 이벤트 dataLayer.push 실패:",
          error,
        );
        return false;
      }
    } else {
      console.warn(
        "[GA Event] ❌ 리포트 이벤트: gtag와 dataLayer 모두 사용 불가능",
        {
          hasGtag: typeof window.gtag === "function",
          hasDataLayer: !!window.dataLayer,
          isArray: Array.isArray(window.dataLayer),
        },
      );
      return false;
    }
  }

  // 일반 이벤트: gtag 함수 사용 (우선)
  if (typeof window.gtag === "function") {
    try {
      // gtag가 no-op인지 확인 (함수 본문이 비어있거나 차단 로그만 있는 경우)
      const gtagString = window.gtag.toString();
      const isNoOp = gtagString.includes("🚫") || gtagString.includes("차단");

      // dataLayer.push를 포함하는 기본 gtag 함수는 정상 함수로 간주
      const isBasicGtag = gtagString.includes("dataLayer.push");

      // 기본 gtag 함수이거나 no-op이 아닌 경우 정상 호출
      if (isNoOp && !isBasicGtag) {
        // no-op인 경우 dataLayer로 fallback
        console.log("[GA Event] gtag가 no-op으로 감지됨, dataLayer로 전환");
        // 아래 dataLayer fallback 로직으로 진행
      } else {
        // gtag 호출 시도
        try {
          window.gtag("event", eventName, params);
          console.log(`[GA Event] ✅ gtag 호출 완료: ${eventName}`);
          return true;
        } catch (gtagError) {
          // gtag 호출 실패 시 dataLayer로 fallback
          console.warn(
            `[GA Event] gtag 호출 실패, dataLayer로 fallback: ${eventName}`,
            gtagError,
          );
          // 아래 dataLayer fallback 로직으로 진행
        }
      }

      // dataLayer fallback (no-op이거나 gtag 호출 실패한 경우)
      if (window.dataLayer && Array.isArray(window.dataLayer)) {
        const eventData = {
          event: eventName,
          ...params,
        };

        // push 전 길이 저장
        const lengthBefore = window.dataLayer.length;

        // dataLayer.push가 래핑되었는지 확인
        const pushString = window.dataLayer.push.toString();
        const isWrapped =
          pushString.includes("🚫") || pushString.includes("차단");

        try {
          if (isWrapped) {
            // 래핑된 경우, 원본 Array.prototype.push를 사용하여 직접 추가
            console.log(
              "[GA Event] 래핑된 dataLayer.push 감지, 원본 push 사용",
            );
            Array.prototype.push.call(window.dataLayer, eventData);
          } else {
            // 정상적인 경우 일반 push 사용
            window.dataLayer.push(eventData);
          }

          // push 후 길이 확인
          const lengthAfter = window.dataLayer.length;
          const isLengthIncreased = lengthAfter > lengthBefore;
          const lastItem = window.dataLayer[lengthAfter - 1] as
            | Record<string, unknown>
            | undefined;
          const isLastItemMatch = lastItem && lastItem.event === eventName;

          if (isLengthIncreased && isLastItemMatch) {
            console.log(`[GA Event] ✅ dataLayer에 푸시 성공: ${eventName}`, {
              lengthBefore,
              lengthAfter,
              eventData,
            });
            return true;
          } else {
            console.error(`[GA Event] ❌ dataLayer 푸시 실패: ${eventName}`, {
              lengthBefore,
              lengthAfter,
              isLengthIncreased,
              isLastItemMatch,
              lastItem,
              eventData,
            });
            return false;
          }
        } catch (error) {
          console.error(
            `[GA Event] ❌ dataLayer.push 예외 발생: ${eventName}`,
            error,
          );
          return false;
        }
      }
    } catch (error) {
      console.error("[GA Event] ❌ gtag 호출 실패:", error);
      return false;
    }
  }

  // dataLayer 직접 사용 (fallback)
  if (window.dataLayer && Array.isArray(window.dataLayer)) {
    try {
      const eventData = {
        event: eventName,
        ...params,
      };

      // push 전 길이 저장
      const lengthBefore = window.dataLayer.length;

      // dataLayer.push가 래핑되었는지 확인
      const pushString = window.dataLayer.push.toString();
      const isWrapped =
        pushString.includes("🚫") || pushString.includes("차단");

      if (isWrapped) {
        // 래핑된 경우, 원본 Array.prototype.push를 사용하여 직접 추가
        console.log("[GA Event] 래핑된 dataLayer.push 감지, 원본 push 사용");
        Array.prototype.push.call(window.dataLayer, eventData);
      } else {
        // 정상적인 경우 일반 push 사용
        window.dataLayer.push(eventData);
      }

      // push 후 길이 확인
      const lengthAfter = window.dataLayer.length;
      const isLengthIncreased = lengthAfter > lengthBefore;
      const lastItem = window.dataLayer[lengthAfter - 1] as
        | Record<string, unknown>
        | undefined;
      const isLastItemMatch = lastItem && lastItem.event === eventName;

      if (isLengthIncreased && isLastItemMatch) {
        console.log(`[GA Event] ✅ dataLayer에 푸시 성공: ${eventName}`, {
          lengthBefore,
          lengthAfter,
          eventData,
        });
        return true;
      } else {
        console.error(`[GA Event] ❌ dataLayer 푸시 실패: ${eventName}`, {
          lengthBefore,
          lengthAfter,
          isLengthIncreased,
          isLastItemMatch,
          lastItem,
          eventData,
        });
        return false;
      }
    } catch (error) {
      console.error("[GA Event] ❌ dataLayer.push 실패:", error);
      return false;
    }
  }

  console.warn(
    "[GA Event] ❌ GA가 로드되지 않았습니다. 이벤트가 추적되지 않습니다.",
  );
  return false;
}

// TypeScript 타입 확장
declare global {
  interface Window {
    gtag?: (
      command: string,
      targetId: string,
      config?: Record<string, unknown>,
    ) => void;
    dataLayer?: unknown[];
  }
}
